import { PropsWithChildren, useEffect, useRef } from "react";
import twemoji from "twemoji";

// Track which nodes have been parsed to avoid duplicate requests
const parsedNodes = new WeakSet<HTMLElement>();

// Check if user is currently selecting text
const isUserSelecting = (): boolean => {
  try {
    const selection = window.getSelection();
    return !!(selection && selection.toString().length > 0);
  } catch {
    return false;
  }
};

export function EmojiProvider({ children }: PropsWithChildren) {
  const pendingNodesRef = useRef<Set<HTMLElement>>(new Set());
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSelectingRef = useRef(false);

  useEffect(() => {
    const parseTarget = (target: HTMLElement | string) => {
      // Don't modify DOM while user is selecting text
      if (isSelectingRef.current || isUserSelecting()) {
        return;
      }
      try {
        twemoji.parse(target, {
          folder: "svg",
          ext: ".svg",
          base: "https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/",
          className: "twemoji",
        });
        if (target instanceof HTMLElement) {
          parsedNodes.add(target);
        }
      } catch (_) {
        // no-op
      }
    };

    // Debounced batch parse to avoid excessive re-parsing during streaming
    const scheduleParseNodes = () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        // Skip if user is selecting
        if (isSelectingRef.current || isUserSelecting()) {
          // Re-schedule for later
          scheduleParseNodes();
          return;
        }
        const nodesToParse = [...pendingNodesRef.current];
        pendingNodesRef.current.clear();
        for (const node of nodesToParse) {
          if (!parsedNodes.has(node)) {
            parseTarget(node);
          }
        }
      }, 200); // Increased debounce to 200ms
    };

    // Track selection state
    const handleSelectionStart = () => {
      isSelectingRef.current = true;
    };
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      isSelectingRef.current = !!(selection && selection.toString().length > 0);
    };

    // Initial parse (only if no selection)
    if (!isUserSelecting()) {
      parseTarget(document.body);
    }

    // Observe DOM changes to parse new content lazily
    const observer = new MutationObserver((mutations) => {
      // Skip if user is selecting
      if (isSelectingRef.current || isUserSelecting()) {
        return;
      }
      let hasNewNodes = false;
      for (const mutation of mutations) {
        if (mutation.type !== "childList") continue;
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          // Skip inputs, textareas, and contenteditable regions
          if (node.tagName === "INPUT" || node.tagName === "TEXTAREA") return;
          if (node.isContentEditable || node.closest("[contenteditable='true']")) return;
          // Avoid reparsing already-parsed emoji images
          if (node.classList.contains("twemoji")) return;
          // Skip if already parsed
          if (parsedNodes.has(node)) return;
          // Queue for debounced parsing
          pendingNodesRef.current.add(node);
          hasNewNodes = true;
        });
      }
      if (hasNewNodes) {
        scheduleParseNodes();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Listen for selection events
    document.addEventListener('selectstart', handleSelectionStart);
    document.addEventListener('selectionchange', handleSelectionChange);

    return () => {
      observer.disconnect();
      document.removeEventListener('selectstart', handleSelectionStart);
      document.removeEventListener('selectionchange', handleSelectionChange);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return <>{children}</>;
}

export default EmojiProvider;


